import { IGroupInfo, IUserInfo } from "@home-health-notify/domain";
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Tabs,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { shouldForwardProp } from "../common/Common.style";
import theme from "../theme";

type CalendarStyleProps = {
  info?: IGroupInfo | IUserInfo;
};

export const CalendarContainer = styled(Container)`
  max-width: 100% !important;
`;

export const CalendarHeaderWrapper = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<CalendarStyleProps>(["info"], prop),
})<CalendarStyleProps>(({ info }) => ({
  display: "grid",
  gridTemplateColumns: info
    ? "max-content auto max-content max-content max-content"
    : "max-content auto max-content max-content",
  alignItems: "center",
}));

export const CalendarHeading = styled(Typography)`
  font-weight: bold;
  font-size: medium;
  text-align: center;
`;

export const BackButton = styled(Button)`
  padding-left: 4px !important;
  display: flex;
  justify-content: space-between;
  gap: 4px;
  text-transform: none;
  align-items: center;
`;

export const StyledTab = styled(Tab)`
  padding: 7px 9px 7px 9px;
  min-height: 0;
  min-width: 0;
  border: 1px solid ${theme.palette.grey[200]};
`;

export const TabsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > * {
    margin-right: 8px;
  }
`;

export const StyledTabs = styled(Tabs)`
  height: 20px;
  padding: 6px;
  align-items: center;

  & .MuiTabs-indicator {
    display: none;
  }
`;

type TabLabelProp = {
  isCurrent: boolean;
  rounded?: boolean;
};

export const TabLabel = styled(Typography, {
  shouldForwardProp: prop =>
    shouldForwardProp<TabLabelProp>(["isCurrent"], prop),
})<TabLabelProp>(({ isCurrent }) => ({
  fontSize: "14px",
  height: "24px",
  fontWeight: 500,
  color: isCurrent ? theme.palette.common.white : theme.palette.common.black,
  textTransform: "none",
}));

export const LeftTab = styled(StyledTab, {
  shouldForwardProp: prop =>
    shouldForwardProp<TabLabelProp>(["isCurrent", "rounded"], prop),
})<TabLabelProp>(({ isCurrent, rounded }) => ({
  borderRadius: rounded ? "20px 0 0 20px" : "4px 0 0 4px",
  borderRight: "0px",
  backgroundColor: isCurrent
    ? theme.palette.primary.main
    : theme.palette.common.white,
  borderColor: isCurrent ? theme.palette.primary.main : theme.palette.grey[200],
}));

export const RightTab = styled(StyledTab, {
  shouldForwardProp: prop =>
    shouldForwardProp<TabLabelProp>(["isCurrent", "rounded"], prop),
})<TabLabelProp>(({ isCurrent, rounded }) => ({
  borderRadius: rounded ? "0 20px 20px 0" : "0 4px 4px 0",
  backgroundColor: isCurrent
    ? theme.palette.primary.main
    : theme.palette.common.white,
  borderColor: isCurrent ? theme.palette.primary.main : theme.palette.grey[200],
}));

export const MiddleTab = styled(StyledTab, {
  shouldForwardProp: prop =>
    shouldForwardProp<TabLabelProp>(["isCurrent"], prop),
})<TabLabelProp>(({ isCurrent }) => ({
  backgroundColor: isCurrent
    ? theme.palette.primary.main
    : theme.palette.common.white,
  borderColor: isCurrent ? theme.palette.primary.main : theme.palette.grey[200],
}));

export const CalendarMapButton = styled(IconButton)`
  background: ${theme.palette.primary.main};

  & > svg {
    color: ${theme.palette.common.white};
  }

  &:hover {
    background: ${theme.palette.primary.main};
  }
`;

type RouteChipProps = {
  markerColor: string;
};

// Shared by the route's Start / End and Stop chips so all three match their
// map pins and each other. min-width leaves slack on short labels, and Chip
// centres by default, which would indent a short label's icon further than a
// long one's.
export const RouteChip = styled(Chip, {
  shouldForwardProp: prop =>
    shouldForwardProp<RouteChipProps>(["markerColor"], prop),
})<RouteChipProps>`
  margin-right: ${theme.spacing(2)};
  min-width: 80px;
  justify-content: flex-start;
  background-color: ${props => props.markerColor};
  color: ${theme.palette.common.white};

  & .MuiChip-icon {
    margin-left: 8px;
    color: ${theme.palette.common.white};
  }
`;

export const CalendarScheduleWrapper = styled(Box)`
  display: flex;
  height: calc(100dvh - 96px);
  margin-top: 8px;
`;

export const ScheduleLabel = styled(Typography)`
  margin: 16px 0;
  font-size: 20px;
  text-align: center;
`;

export const ScheduleWrapper = styled(Box)`
  flex: 0 0 400px;
  padding: 0 10px;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

export const ScheduleListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ScheduleItemWrapper = styled(Box)`
  position: relative;
`;

export const ScheduleItem = styled(Box)`
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${theme.palette.grey[200]};
  padding: 15px;
`;

export const ScheduleDateTime = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Date = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: ${theme.palette.primary.main};
`;

export const Title = styled(Typography)`
  font-size: 14px;
  font-weight: 500;
  margin: 10px 0;
`;

export const ScheduleTime = styled(Box)`
  font-size: 14px;
  font-weight: 400;
`;

export const CalendarWrapper = styled(Box)`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type CalendarMapContainerProps = {
  showHeader?: boolean;
};

export const CalendarMapContainer = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<CalendarMapContainerProps>(["showHeader"], prop),
})<CalendarMapContainerProps>(({ showHeader }) => ({
  height: "100%",
  maxWidth: "100% !important",
  padding: "0 !important",
  margin: "0 !important",
  display: "flex",
  width: "100%",
}));

export const CalendarMapSidebar = styled(Box)`
  width: 40%; /* Adjust width as needed */
  height: 100%; /* Full height of the container */
  overflow-y: auto; /* Enable vertical scrolling */
  overflow-x: hidden; /* Prevent horizontal scrolling */
`;

export const MapContainer = styled(Box)`
  width: 100%;
  background: ${theme.palette.grey[200]};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const CloseModalButton = styled(IconButton)`
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
`;

export const SectionHeader = styled(Box)`
  background: ${theme.palette.grey[200]};
  border-radius: 4px;
  padding: 5px 12px;
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 500;
`;

export const MapCalendarWrapper = styled(Box)`
  height: 350px;
  border: 1px solid ${theme.palette.grey[200]};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
`;

export const Section = styled(Box)`
  margin-bottom: 15px;
`;

export const LupaModalContent = styled(Box)`
  padding: 10px 16px;
`;

export const LupaInfo = styled(Typography)`
  font-size: 15px;
  font-weight: 500;
`;

export const LupaButton = styled(Button)`
  text-transform: none;
  height: 40px;
  margin-right: 8px;
`;

export const HighlightedOverlay = styled(Box)`
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 1;
  background-color: ${theme.message?.infoBubble ?? theme.palette.grey[200]};
  border-radius: 4px;
`;
